import {
  NA,
  encounterDate,
  encounterProvider,
  encounterTypeLabel,
  formatDate,
  reasonCodings,
} from "@/lib/patientChart";

export const SOAP_TOKEN_RE = /@([a-z][a-z0-9_]*)@/gi;

export const SOAP_FIELD_GROUPS = [
  {
    id: "patient",
    label: "Patient Information",
    fields: [
      { token: "patientname", label: "Patient Name" },
      { token: "age", label: "Age" },
      { token: "dob", label: "DOB" },
      { token: "gender", label: "Gender" },
      { token: "mrn", label: "MRN" },
    ],
  },
  {
    id: "encounter",
    label: "Encounter Information",
    fields: [
      { token: "dos", label: "Date of Service" },
      { token: "provider", label: "Provider" },
      { token: "encountertype", label: "Encounter Type" },
      { token: "chiefcomplaint", label: "Chief Complaint" },
    ],
  },
  {
    id: "clinical",
    label: "Clinical Information",
    fields: [
      { token: "diagnoses", label: "Diagnoses" },
      { token: "medications", label: "Medications" },
      { token: "allergies", label: "Allergies" },
      { token: "assessment", label: "Assessment" },
      { token: "plan", label: "Plan" },
    ],
  },
];

export const SOAP_SMART_PHRASES = [
  {
    id: "patient-header",
    label: "Patient header",
    body: "Patient: @patientname@\nAge: @age@\nMRN: @mrn@\n",
  },
  {
    id: "hpi-intro",
    label: "HPI introduction",
    body: "The patient is a @age@ year old @gender@ presenting for evaluation of @chiefcomplaint@.\n",
  },
];

export const SOAP_KNOWN_TOKENS = SOAP_FIELD_GROUPS.flatMap((group) =>
  group.fields.map((field) => field.token),
);

export function soapFieldToken(token) {
  return `@${String(token || "").replace(/@/g, "").toLowerCase()}@`;
}

function soapBlank(value) {
  if (value == null || value === "" || value === NA) return "";
  return String(value);
}

function diagnosisRowsFromNote(noteDiagnoses = []) {
  return (noteDiagnoses || [])
    .filter((item) => item?.code)
    .map((item) => ({
      code: item.code,
      display: item.description || item.display || "",
      system: item.system || "",
    }));
}

export function soapValuesFromContext({
  fields,
  encounter,
  descriptionByCode = {},
  noteDiagnoses,
} = {}) {
  const fromNote = diagnosisRowsFromNote(noteDiagnoses);
  const reasons = fromNote.length ? fromNote : reasonCodings(encounter);
  const chiefComplaint =
    (encounter?.reasonCode || [])
      .map((rc) => rc.text || rc.coding?.[0]?.display || rc.coding?.[0]?.code)
      .filter(Boolean)[0] ||
    reasons[0]?.display ||
    reasons[0]?.code ||
    "";
  const diagnoses = reasons
    .map((reason) => {
      const description = descriptionByCode[reason.code] || reason.display;
      if (description && description !== NA) return `${reason.code} — ${description}`;
      return reason.code || "";
    })
    .filter(Boolean)
    .join("; ");

  return {
    patientname: soapBlank(fields?.name),
    age: fields?.age != null ? String(fields.age) : "",
    dob: fields?.birthDate ? soapBlank(formatDate(fields.birthDate)) : "",
    gender: soapBlank(fields?.gender),
    mrn: soapBlank(fields?.mrn),
    dos: soapBlank(formatDate(encounterDate(encounter))),
    provider: soapBlank(encounterProvider(encounter)),
    encountertype: soapBlank(encounterTypeLabel(encounter)),
    chiefcomplaint: soapBlank(chiefComplaint),
    diagnoses: soapBlank(diagnoses),
    medications: "",
    allergies: "",
    assessment: "",
    plan: "",
  };
}

export function resolveSoapFields(text, valuesByToken = {}) {
  if (!text) return "";
  return String(text).replace(SOAP_TOKEN_RE, (match, token) => {
    const key = String(token).toLowerCase();
    if (!Object.prototype.hasOwnProperty.call(valuesByToken, key)) {
      if (SOAP_KNOWN_TOKENS.includes(key)) return "";
      return match;
    }
    const value = valuesByToken[key];
    return value == null ? "" : String(value);
  });
}

export function countSoapText(text) {
  const value = String(text || "");
  const words = value.trim() ? value.trim().split(/\s+/).length : 0;
  return { words, characters: value.length };
}
